import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { useAppContext } from "../context/AppContext";
import emailjs from "emailjs-com";

const Home = () => {
  const { products, bestSellers } = useAppContext();
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#inquiry") {
      const el = document.querySelector(location.hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [location]);

  const getRandomProducts = (count, excludeIds = []) => {
    const available = products.filter((p) => !excludeIds.includes(p.id));
    return [...available].sort(() => 0.5 - Math.random()).slice(0, count);
  };

  const [featuredCount, setFeaturedCount] = useState(4);
  const [recommendedCount, setRecommendedCount] = useState(4);
  const [dealsCount, setDealsCount] = useState(4);
  const [trendsCount, setTrendsCount] = useState(4);

  const featuredProducts = products.filter((p) => p.isFeatured);
  const recommendedProducts = getRandomProducts(
    12,
    bestSellers.map((p) => p.id)
  );
  const dealProducts = products
    .filter((p) => p.price > 80)
    .sort((a, b) => b.price - a.price);
  const trendProducts = getRandomProducts(12, [
    ...bestSellers.map((p) => p.id),
    ...recommendedProducts.map((p) => p.id),
  ]);

  const [inquiryData, setInquiryData] = useState({
    name: "",
    email: "",
    user_email: "",
    subject: "",
    phone: "",
    message: "",
  });

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setInquiryData((prev) => ({
      ...prev,
      [id]: value,
      ...(id === "email" && { user_email: value }),
    }));
  };

  const testimonials = [
    {
      id: 1,
      name: "Sarah Johnson",
      role: "Interior Designer",
      content:
        "The quality of these home decors is exceptional. My clients are always thrilled with the transformation these products bring to their spaces.",
      rating: 5,
      image: "/assets/images/testimonial-2.jpg",
    },
    {
      id: 2,
      name: "Michael Chen",
      role: "Homeowner",
      content:
        "I've purchased several items from this store and each one has exceeded my expectations. The customer service is outstanding too!",
      rating: 5,
      image: "/assets/images/testimonial-1.jpg",
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      role: "Blogger",
      content:
        "As someone who reviews home decor products regularly, I can confidently say this store offers some of the best value for money in the market.",
      rating: 4,
      image: "/assets/images/testimonial-3.jpg",
    },
  ];

  const blogPosts = [
    {
      title: "10 Sustainable Decor Tips for Your Home",
      description:
        "Discover eco-friendly ways to enhance your home's aesthetic with sustainable materials and practices.",
      image:
        "https://images.unsplash.com/photo-1517705008128-361805f42e86?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      alt: "Sustainable Decor Tips",
    },
    {
      title: "Why Choose Organic Materials for Your Home",
      description:
        "Learn about the environmental and health benefits of using organic materials in home decor.",
      image:
        "https://images.unsplash.com/photo-1598928636135-d146006ff4be?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      alt: "Benefits of Organic Materials",
    },
    {
      title: "Creating Eco-Friendly Outdoor Spaces",
      description:
        "Tips for designing sustainable and beautiful outdoor areas for relaxation and entertainment.",
      image:
        "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      alt: "Eco-Friendly Outdoor Spaces",
    },
  ];

  return (
    <>
      <section className="hero" id="home">
        <div className="hero-content">
          <h1>Premium Home Decors</h1>
          <p>
            Experience the perfect blend of elegance, lasting comfort, and
            superior quality with our premium home textiles.
          </p>
          <Link to="/products" className="btn">
            Shop Now
          </Link>
        </div>
      </section>

      <section className="py-16 bg-gray-50" id="categories">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-10">
            Our Categories
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {[
              {
                name: "Curtains",
                image: "/assets/images/curtains-1.jpg",
                category: "curtains",
              },
              {
                name: "Sofa Covers",
                image: "/assets/images/sofa-covers-1.jpg",
                category: "sofa-covers",
              },
              {
                name: "Pillows",
                image: "/assets/images/pillow-1.jpg",
                category: "pillows",
              },
              {
                name: "Bedding",
                image: "/assets/images/bedding-1.jpg",
                category: "bedding",
              },
            ].map(({ name, image, category }) => (
              <Link
                key={category}
                to={`/products?category=${category}`}
                className="rounded overflow-hidden shadow hover:shadow-lg bg-white transition transform hover:-translate-y-1"
              >
                <img
                  src={image}
                  alt={name}
                  className="w-full h-48 object-cover"
                />
                <div className="p-4 text-center">
                  <h3 className="text-lg font-semibold mb-2">{name}</h3>
                  <button className="text-sm text-indigo-600 hover:underline">
                    View Collection
                  </button>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {[
        {
          title: "Featured Products",
          products: featuredProducts,
          count: featuredCount,
          setCount: setFeaturedCount,
        },
        {
          title: "Best Sellers",
          products: bestSellers,
          count: recommendedCount,
          setCount: setRecommendedCount,
        },
        {
          title: "Recommended For You",
          products: recommendedProducts,
          count: recommendedCount,
          setCount: setRecommendedCount,
        },
        {
          title: "Deals of the Day",
          products: dealProducts,
          count: dealsCount,
          setCount: setDealsCount,
          subtitle: "Limited time offers - don't miss out!",
        },
        {
          title: "Follow Trends In Decor",
          products: trendProducts,
          count: trendsCount,
          setCount: setTrendsCount,
          subtitle: "Latest styles for your home",
        },
      ].map(({ title, subtitle, products, count, setCount }, index) => (
        <section
          key={index}
          className={`py-16 ${index % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
        >
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold mb-2">{title}</h2>
              {subtitle && <p className="text-gray-500">{subtitle}</p>}
            </div>
            <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {products.slice(0, count).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
            {count < products.length && (
              <div className="text-center mt-10">
                <button
                  className="btn bg-indigo-600 text-white px-6 py-2 rounded-full hover:bg-indigo-700 transition"
                  onClick={() => setCount((prev) => prev + 4)}
                >
                  Load More
                </button>
              </div>
            )}
          </div>
        </section>
      ))}

      <section className="py-20 bg-white" id="about">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2">
              <img
                src="/assets/images/about-us.jpg"
                alt="About Our Store"
                className="rounded-lg shadow-xl w-full h-auto"
              />
            </div>
            <div className="lg:w-1/2">
              <h2 className="text-4xl font-bold text-gray-800 mb-6">
                Our Story
              </h2>
              <p className="text-lg text-gray-600 mb-4">
                Founded in 2010, Premium Home Decors began as a small family
                business with a passion for bringing beautiful, high-quality
                home textiles to discerning customers.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Today, we've grown into a trusted name in home decor, known for
                our curated collections, exceptional craftsmanship, and
                commitment to customer satisfaction. Every product in our store
                is carefully selected to ensure it meets our high standards of
                quality and design.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-indigo-50 p-4 rounded-lg">
                  <h3 className="font-bold text-indigo-700 text-xl">10,000+</h3>
                  <p className="text-gray-600">Happy Customers</p>
                </div>
                <div className="bg-indigo-50 p-4 rounded-lg">
                  <h3 className="font-bold text-indigo-700 text-xl">500+</h3>
                  <p className="text-gray-600">Quality Products</p>
                </div>
              </div>
              <Link
                to="/about"
                className="inline-block bg-indigo-600 text-white font-semibold py-3 px-6 rounded-lg shadow hover:bg-indigo-700 transition"
              >
                Learn More About Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-indigo-700 text-white" id="testimonials">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">What Our Customers Say</h2>
            <p className="text-xl text-indigo-100 max-w-2xl mx-auto">
              Don't just take our word for it - hear from our satisfied
              customers
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-white rounded-xl shadow-lg p-6 text-gray-800"
              >
                <div className="flex items-center mb-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h3 className="font-bold">{testimonial.name}</h3>
                    <p className="text-sm text-indigo-600">
                      {testimonial.role}
                    </p>
                  </div>
                </div>
                <p className="mb-4 italic">"{testimonial.content}"</p>
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      className={`w-5 h-5 ${
                        i < testimonial.rating
                          ? "text-yellow-400"
                          : "text-gray-300"
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-16 bg-gray-50" id="blog">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold mb-2">Home Decor Living Blog</h2>
            <p className="text-gray-500">
              Latest tips and trends for your home
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {blogPosts.map((post, index) => (
              <div
                key={index}
                className="bg-white rounded-lg overflow-hidden shadow hover:shadow-lg transition"
              >
                <img
                  src={post.image}
                  alt={post.alt}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="font-semibold text-xl mb-3">{post.title}</h3>
                  <p className="text-gray-600 mb-4">{post.description}</p>
                  <a
                    href="#"
                    className="text-indigo-600 font-medium hover:text-indigo-800 transition"
                  >
                    Read More →
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 bg-indigo-50" id="contact">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-indigo-700 mb-4">
              Get in Touch
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Have a question or need help? We'd love to hear from you.
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              emailjs
                .send(
                  process.env.REACT_APP_EMAILJS_SERVICE_ID,
                  process.env.REACT_APP_EMAILJS_ADMIN_TEMPLATE_ID,
                  inquiryData,
                  process.env.REACT_APP_EMAILJS_PUBLIC_KEY
                )
                .then(() => {
                  // Send auto-reply email
                  return emailjs.send(
                    process.env.REACT_APP_EMAILJS_SERVICE_ID,
                    process.env.REACT_APP_EMAILJS_USER_AUTO_REPLY_TEMPLATE_ID,
                    inquiryData,
                    process.env.REACT_APP_EMAILJS_PUBLIC_KEY
                  );
                })
                .then(() => {
                  alert("Message sent successfully!");
                  setInquiryData({
                    name: "",
                    email: "",
                    user_email: "",
                    subject: "",
                    phone: "",
                    message: "",
                  });
                })
                .catch((error) => {
                  console.error("EmailJS Error:", error);
                  alert("Failed to send message. Try again.");
                });
            }}
            className="max-w-3xl mx-auto bg-white rounded-xl shadow-xl p-10 space-y-6"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  placeholder="John Doe"
                  value={inquiryData.name}
                  onChange={handleInputChange}
                  required
                  className="mt-1 w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  placeholder="you@example.com"
                  value={inquiryData.email}
                  onChange={handleInputChange}
                  required
                  className="mt-1 w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="subject"
                className="block text-sm font-medium text-gray-700"
              >
                Subject
              </label>
              <input
                type="text"
                id="subject"
                placeholder="Place order"
                value={inquiryData.subject}
                onChange={handleInputChange}
                required
                className="mt-1 w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700"
              >
                Phone
              </label>
              <input
                type="tel"
                id="phone"
                placeholder="+1 234 567 890"
                value={inquiryData.phone}
                onChange={handleInputChange}
                className="mt-1 w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label
                htmlFor="message"
                className="block text-sm font-medium text-gray-700"
              >
                Message
              </label>
              <textarea
                id="message"
                rows="5"
                placeholder="Write your message..."
                value={inquiryData.message}
                onChange={handleInputChange}
                required
                className="mt-1 w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="text-center">
              <button
                type="submit"
                className="w-full sm:w-auto inline-block bg-indigo-600 text-white font-semibold py-3 px-6 rounded-lg shadow hover:bg-indigo-700 transition-all duration-300"
              >
                Send Message
              </button>
            </div>
          </form>
        </div>
      </section>
    </>
  );
};

export default Home;
